<template>
  <hs-modal v-model="open" title="Remove Member" size="sm" @close="$emit('close')">
    <div class="rmcm-body">
      <div class="rmcm-icon-wrap">
        <vue-feather type="user-x" size="22" class="rmcm-icon" />
      </div>

      <div class="rmcm-info">
        <p class="rmcm-info__name">Remove {{ member.user_name || "this member" }}?</p>
        <p class="rmcm-info__email">{{ member.user_email || `User #${member.user_id}` }}</p>
      </div>

      <div class="rmcm-meta">
        <div class="rmcm-meta__row">
          <span class="rmcm-meta__label">Role</span>
          <span class="rmcm-meta__value">{{ member.role }}</span>
        </div>
        <div class="rmcm-meta__row">
          <span class="rmcm-meta__label">Joined</span>
          <span class="rmcm-meta__value">{{ formatDate(member.created_at) }}</span>
        </div>
      </div>

      <hs-alert
        tone="warning"
        title="This action cannot be undone"
        message="The member will lose access to this group and all its resources. They can be re-invited later if needed."
      />

      <hs-alert v-if="errorMessage" tone="danger" :message="errorMessage" />
    </div>

    <template #footer>
      <div class="rmcm-footer">
        <hs-button variant="ghost" type="button" :disabled="loading" @click="$emit('close')">Cancel</hs-button>
        <hs-button variant="primary" icon="user-x" tone="danger" :loading="loading" @click="handleRemove">
          Remove Member
        </hs-button>
      </div>
    </template>
  </hs-modal>
</template>

<script>
import { ref } from "vue";
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import { HsModal, HsButton, HsAlert } from "@/components/ui";

export default {
  name: "RemoveMemberConfirmationModal",
  components: { HsModal, HsButton, HsAlert },
  props: {
    groupId: { type: Number, required: true },
    member: { type: Object, required: true },
  },
  emits: ["close", "removed"],
  setup(props, { emit }) {
    const open = ref(true);
    const loading = ref(false);
    const errorMessage = ref("");

    const formatDate = (dateString) => {
      if (!dateString) return "Unknown";
      return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    const handleRemove = async () => {
      loading.value = true;
      errorMessage.value = "";
      try {
        await http.delete(`/v2/groups/${props.groupId}/members/${props.member.user_id}`);
        toast.success("Member removed successfully");
        emit("removed");
        emit("close");
      } catch (err) {
        console.error("Failed to remove member", err);
        errorMessage.value = err?.response?.data?.error?.message || err.message || "Unable to remove member.";
      } finally {
        loading.value = false;
      }
    };

    return { open, loading, errorMessage, formatDate, handleRemove };
  },
};
</script>

<style scoped>
.rmcm-body {
  display: grid;
  gap: 1rem;
}

.rmcm-icon-wrap {
  align-items: center;
  background: color-mix(in srgb, #ef4444 10%, transparent);
  border: 1px solid color-mix(in srgb, #ef4444 25%, transparent);
  border-radius: 50%;
  display: flex;
  height: 3rem;
  justify-content: center;
  margin: 0 auto;
  width: 3rem;
}

.rmcm-icon {
  color: #ef4444;
}

.rmcm-info {
  text-align: center;
}

.rmcm-info__name {
  color: var(--hs-text);
  font-size: 1rem;
  font-weight: 750;
  margin: 0;
}

.rmcm-info__email {
  color: var(--hs-muted);
  font-size: 0.83rem;
  margin: 0.25rem 0 0;
}

.rmcm-meta {
  background: color-mix(in srgb, var(--hs-bg-soft) 72%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 10px;
  display: grid;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
}

.rmcm-meta__row {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.rmcm-meta__label {
  color: var(--hs-muted);
  font-size: 0.8rem;
}

.rmcm-meta__value {
  color: var(--hs-text);
  font-size: 0.8rem;
  font-weight: 650;
}

.rmcm-footer {
  display: flex;
  gap: 0.6rem;
  justify-content: flex-end;
}
</style>
